<template>
  <div class="inci-list">
    <h2 class="inci-list__title u-typography-helvetica u-text-display u-text-display--s">
      {{ $t('discover.expandableList.section.inciList.title') }}
    </h2>

    <div v-if="shouldDisplayDetailedInciList">
      <template v-if="hasDetailedConfigActives">
        <h3 class="inci-list__subtitle u-typography-helvetica u-text u-text--link u-text--s">
          {{ $t('discover.expandableList.section.inciList.activeIngredients') }}
        </h3>

        <p
          v-for="(inciConfig, i) of inciListsDetailedConfig.actives"
          :key="inciConfig.title"
          class="u-typography-helvetica u-text u-text--s"
          :class="{ 'inci-list__paragraph': i === inciListsDetailedConfig.actives.length - 1 }"
        >
          <b>{{ $t(inciConfig.title) }}</b
          >: {{ inciConfig.body }}.
        </p>
      </template>

      <h3
        v-if="hasDetailedConfigActives"
        class="inci-list__subtitle u-typography-helvetica u-text u-text--link u-text--s"
      >
        {{ $t('discover.expandableList.section.inciList.inactiveIngredients') }}
      </h3>

      <p class="inci-list__paragraph u-typography-helvetica u-text u-text--s">
        {{ inciListsDetailedConfig.inactives }}
        <span v-if="inciListsDetailedConfig.excipients"
          >Excipients from actives: {{ inciListsDetailedConfig.excipients }}</span
        >
      </p>
    </div>

    <div v-else>
      <div v-for="inciConfig of inciListsCommonConfig" :key="inciConfig.title">
        <h3
          v-if="inciConfig.title"
          class="inci-list__subtitle u-typography-helvetica u-text u-text--link u-text--s"
        >
          {{ $t(inciConfig.title) }}
        </h3>
        <p class="inci-list__paragraph u-typography-helvetica u-text u-text--s">
          {{ inciConfig.body }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { uniq } from 'ramda';
import { mapActions, mapGetters, mapState } from 'vuex';

import {
  getInciListActiveIngredients,
  getInciListExcipients,
  getInciListInactiveIngredients,
  getInciListUsage
} from '@/models/core/InciList';
import {
  getProductOptionsDefaultOption,
  getProductOptionsOptionByFormulaIdOrDefault
} from '@/models/core/ProductOptions';
import { isProductPackGenericEyeSerum } from '@/models/core/ProductPack';

import { types } from '@/store/types';
import { COUNTRY_ISO } from '@/constants/countries';
import { FORMULA_USAGE } from '@/constants/treatment';

const USAGE_TITLE = {
  [FORMULA_USAGE.MORNING_ONLY]: 'discover.product.serum.amSerum',
  [FORMULA_USAGE.EVENING_ONLY]: 'discover.product.serum.pmSerum',
  [FORMULA_USAGE.MORNING_AND_EVENING]: 'discover.product.serum.amPmSerum'
};

export default {
  name: 'ProductInciList',
  props: {
    productName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      inciLists: []
    };
  },
  computed: {
    ...mapState({
      selectedEyeContourGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapGetters({
      country: types.getters.DELIVERY_COUNTRY,
      allProductOptionsSets: types.getters.ALL_PRODUCT_OPTIONS_SETS
    }),
    product() {
      const productOptions = this.allProductOptionsSets[this.productName];
      const defaultOption = getProductOptionsDefaultOption(productOptions);

      if (isProductPackGenericEyeSerum(defaultOption)) {
        return getProductOptionsOptionByFormulaIdOrDefault(
          this.selectedEyeContourGenericFormulaId,
          productOptions
        );
      }

      return defaultOption;
    },
    shouldDisplayDetailedInciList() {
      return this.country.toUpperCase() === COUNTRY_ISO.AUSTRALIA;
    },
    inciListsCommonConfig() {
      return this.inciLists.map(inciList => {
        const usage = getInciListUsage(inciList);
        const title = usage ? USAGE_TITLE[usage] : '';
        const ingredientsList = getInciListInactiveIngredients(inciList).join(', ');
        const body = `${this.$t('discover.common.ingredients')}: ${ingredientsList}.`;

        return {
          title,
          body
        };
      });
    },
    hasDetailedConfigActives() {
      return this.inciListsDetailedConfig && this.inciListsDetailedConfig.actives.length > 0;
    },
    inciListsDetailedConfig() {
      const actives = this.inciLists
        .map(inciList => {
          const usage = getInciListUsage(inciList);
          const title = USAGE_TITLE[usage];
          const body = getInciListActiveIngredients(inciList).join(', ');

          return { title, body };
        })
        .filter(({ body }) => !!body);

      const inactives = uniq(this.inciLists.flatMap(getInciListInactiveIngredients)).join(', ');
      const excipients = uniq(this.inciLists.flatMap(getInciListExcipients)).join(', ');

      return {
        actives,
        inactives: `${inactives}.`,
        excipients: excipients ? `${excipients}.` : ''
      };
    }
  },
  async created() {
    this.inciLists = await this.getInciLists(this.product);
  },
  methods: {
    ...mapActions({
      getInciLists: types.actions.GET_INCI_LISTS
    })
  }
};
</script>

<style lang="scss" scoped>
.inci-list {
  &__title {
    margin-bottom: 32px;
  }

  &__subtitle {
    margin-bottom: 16px;
  }

  &__paragraph {
    margin-bottom: 32px;
  }
}
</style>
